import React, { useRef, useState } from 'react';
import { BlobServiceClient } from '@azure/storage-blob';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const ALLOWED_EXTENSIONS = [
  'doc',
  'docx',
  'pdf',
  'xls',
  'xlsx',
  'md',
  'txt',
  'csv',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'bmp',
  'webp',
  'svg',
];
const MAX_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const INPUT_CONTAINER = 'general-chat';

export interface UploadedBlobFile {
  filePath: string; // e.g. "general-chat/userName/test.pdf"
  fileName: string; // e.g. "test.pdf"
  blobName: string; // relative to container, e.g. "userName/test.pdf"
}

interface FileUploadInputProps {
  userName: string;
  onUploadSuccess: (file: UploadedBlobFile) => void;
  disabled?: boolean;
}

const FileUploadInput: React.FC<FileUploadInputProps> = ({ userName, onUploadSuccess, disabled = false }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const sasToken = process.env.REACT_APP_STORAGE_SAS_KEY || '';
  const storageAccount = process.env.REACT_APP_STORAGE_ACCOUNT || '';

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const validateFile = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return `File type ".${ext}" is not allowed. Allowed types: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_SIZE_BYTES) {
      return 'File size exceeds the 50 MB limit.';
    }
    return null;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so the same file can be re-selected if needed
    e.target.value = '';
    if (!file) return;

    const validationError = validateFile(file);
    if (validationError) {
      showSnackbar(validationError, 'error');
      return;
    }

    try {
      setUploading(true);
      const blobServiceClient = new BlobServiceClient(`https://${storageAccount}.blob.core.windows.net?${sasToken}`);
      const containerClient = blobServiceClient.getContainerClient(INPUT_CONTAINER);
      const blobName = `${userName}/${file.name}`;
      const blockBlobClient = containerClient.getBlockBlobClient(blobName);
      await blockBlobClient.uploadData(file, {
        blobHTTPHeaders: { blobContentType: file.type },
      });

      const filePath = `${INPUT_CONTAINER}/${blobName}`;
      showSnackbar(`"${file.name}" uploaded successfully.`, 'success');
      onUploadSuccess({ filePath, fileName: file.name, blobName });
    } catch (err: any) {
      console.error('File upload error:', err);
      showSnackbar(err?.message || 'Failed to upload file. Please try again.', 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 5000 : 3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <Tooltip title={disabled ? 'Remove current file before uploading another' : 'Attach File'} placement="top">
        <span>
          <IconButton
            onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
            disabled={disabled || uploading}
            size="small"
            color="primary"
            aria-label="Attach file"
          >
            {uploading ? <CircularProgress size={18} color="inherit" /> : <AttachFileIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <input
        type="file"
        accept=".doc,.docx,.pdf,.xls,.xlsx,.md,.txt,.csv,.png,.jpg,.jpeg,.gif,.bmp,.webp,.svg"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleFileSelect}
      />
    </>
  );
};

export default FileUploadInput;
